import React, { Fragment, useEffect, useState } from "react";
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../components/Admin/Reusable/Breadcrumb";
import CachedIcon from "@mui/icons-material/Cached";
import {
  createNewAdministrator,
  fetchAdministratorById,
  updateAdministrator,
} from "../actions/administrator";
import SubmitButtonForm from "../components/Admin/Reusable/Form/SubmitButtonForm";
import FormTitleMain from "../components/Admin/Reusable/FormTitleMain";
import InputField from "../components/Admin/Reusable/InputField";

import SelectStatus from "../components/Admin/Reusable/Form/SelectStatus";

import { useSelector } from "react-redux";
import { basicFormValidationHandler } from "../utils";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import { administratorActions } from "../reducers/administrator";
import GlobalProgressOverlay from "../components/Admin/Reusable/GlobalProgressOverlay";
import {
  ADMINISTRATOR_TYPE,
  ADMINISTRATOR_TYPE_LABELS,
  EMPLOYEE_TYPE,
} from "../utils/auth";

function generateRandomPassword() {
  const length = Math.floor(Math.random() * 3) + 13; // Dužina između 9 i 11 karaktera
  const specialChars = "-_~.#!@&";

  const allChars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789" +
    specialChars;

  const getRandomChar = (charSet) => {
    const randomIndex = Math.floor(Math.random() * charSet.length);
    return charSet.charAt(randomIndex);
  };

  let password = "";
  password += getRandomChar("ABCDEFGHIJKLMNOPQRSTUVWXYZ"); // Prvo slovo je veliko
  password += getRandomChar("0123456789"); // Barem jedan broj
  password += getRandomChar(specialChars); // Barem jedan specijalni karakter

  while (password.length < length) {
    password += getRandomChar(allChars);
  }

  return password;
}

const AdministratorAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();

  const [errors, setErrors] = useState({});
  const { administratorDetail, loading, success, actionType } = useSelector(
    (state) => state.administratorReducer
  );

  const initialState = {
    name: "",
    surname: "",
    email: "",
    password: "",
    type: ADMINISTRATOR_TYPE,
    active: true,
  };

  //osnovi podaci
  const [data, setData] = useState(initialState);
  const [progress, setProgress] = useState(0);
  const [showLoader, setShowLoader] = useState(false);

  //breadcrumbs
  const translateForm = "administratorForm";
  const translateModule = "administrator";
  const pageType = id ? "Edit" : "Add";
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  useEffect(() => {
    if (!id) {
      // Ako `id` ne postoji, očisti stanje za novu kreaciju
      setData(initialState);
    } else {
      // Ako `id` postoji, učitaj podatke za korisnika
      dispatch(fetchAdministratorById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (administratorDetail && id) {
      setData({
        ...initialState,
        ...administratorDetail,
        type: administratorDetail.type || ADMINISTRATOR_TYPE,
      });
    }
  }, [administratorDetail, id, dispatch]);

  useEffect(() => {
    let interval;
    let timeout;

    if (loading) {
      setShowLoader(true);
      setProgress(0);

      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 5;
        });
      }, 100);
    } else if (!loading && showLoader) {
      setProgress(100);

      timeout = setTimeout(() => {
        setShowLoader(false);
        setProgress(0);
      }, 2000);
    }

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [loading]);

  // useEffect(() => {
  //   if (success) {
  //     const timeout = setTimeout(() => {
  //       navigate("/administrator-list");
  //       toast.success(
  //         "Uspešno ste kreirali/izmenili administratora",{ theme: "colored" });
  //           dispatch(administratorActions.clearError());
  //     }, 2000);
  //     return () => clearTimeout(timeout);
  //   }else{
  //     dispatch(administratorActions.clearError());
  //   }
  // }, [success]);
  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => {
        navigate("/administrator-list");

        if (actionType === "create") {
          const createdEntity =
            data.type === EMPLOYEE_TYPE ? "zaposlenog" : "administratora";
          toast.success(`Uspešno ste kreirali novog ${createdEntity}`, {
            theme: "colored",
          });
        } else if (actionType === "update") {
          toast.success("Uspešno ste izmenili administratora", {
            theme: "colored",
          });
        }

        dispatch(administratorActions.clearError());
      }, 2000);

      return () => clearTimeout(timeout);
    } else {
      dispatch(administratorActions.clearError());
    }
  }, [success]);

  const userValidationHandler = () => {
    const { name, surname, email, password, type } = data;

    let errorsObject = {};
    if (id) {
      errorsObject = basicFormValidationHandler({
        name,
        surname,
        email,
        type,
      });
    } else {
      errorsObject = basicFormValidationHandler({
        name,
        surname,
        email,
        password,
        type,
      });
    }

    setErrors(errorsObject);
    if (!isEmpty(errorsObject))
      toast.error("Morate popuniti sva obavezna polja!", {
        theme: "colored",
      });
    return !isEmpty(errorsObject);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (userValidationHandler()) {
      return;
    }

    if (id) {
      data.id = id;
    }

    if (id) {
      dispatch(updateAdministrator(data, navigate, id));
    } else {
      dispatch(createNewAdministrator(data, navigate));
    }
  };

  const handleInputChange = (eOrName, maybeValue) => {
    if (typeof eOrName === "string") {
      const name = eOrName;
      const value = maybeValue;
      setData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    // standardni onChange event
    const { name, type, value, checked } = eOrName.target || {};
    setData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleGeneratePassword = () => {
    const newPassword = generateRandomPassword();
    setData((prevUser) => ({
      ...prevUser,
      password: newPassword,
    }));
  };

  return (
    <Fragment>
      <Breadcrumb
        breadcrumbItems={breadcrumbItems}
        type={`${translateModule}${pageType}`}
        id={id}
        pageType={pageType}
      />
      {showLoader && <GlobalProgressOverlay progress={progress} />}
      <Container className="administrator-container">
        <Row>
          <Col lg={12}>
            <form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col lg={12}>
                  <div className="text-end mb-2">
                    <SubmitButtonForm id={id} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={9}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>
                          <FormTitleMain />
                        </Card.Header>
                        <Card.Body>
                          <div className="blog-holder-admin-left-side-text">
                            <Row>
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <InputField
                                  label={t(`${translateForm}.name`)}
                                  name="name"
                                  value={data.name}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col>
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <InputField
                                  label="Prezime"
                                  name="surname"
                                  value={data.surname}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col>
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <InputField
                                  label={t(`${translateForm}.email`)}
                                  name="email"
                                  value={data.email}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col>
                              <Col lg={4} md={12} xs={12} className="mb-4">
                                <FormControl
                                  fullWidth
                                  size="small"
                                  required
                                  error={!!errors.type}
                                >
                                  <InputLabel id="administrator-type-label">
                                    Tip
                                  </InputLabel>
                                  <Select
                                    labelId="administrator-type-label"
                                    label="Tip"
                                    name="type"
                                    value={data.type || ""}
                                    onChange={handleInputChange}
                                  >
                                    <MenuItem value={ADMINISTRATOR_TYPE}>
                                      {
                                        ADMINISTRATOR_TYPE_LABELS[
                                          ADMINISTRATOR_TYPE
                                        ]
                                      }
                                    </MenuItem>
                                    <MenuItem value={EMPLOYEE_TYPE}>
                                      {ADMINISTRATOR_TYPE_LABELS[EMPLOYEE_TYPE]}
                                    </MenuItem>
                                  </Select>
                                  {!!errors.type && (
                                    <FormHelperText>
                                      {errors.type}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Col>
                              {!id && (
                                <Col lg={4} md={12} xs={12} className="mb-4">
                                  <InputField
                                    label={t(`${translateForm}.password`)}
                                    name="password"
                                    value={data.password}
                                    onChange={handleInputChange}
                                    error={errors}
                                    errorsHandler={setErrors}
                                    fullWidth
                                    size="small"
                                    required
                                    inputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Tooltip title="Generisi lozinku">
                                            <CachedIcon
                                              style={{ cursor: "pointer" }}
                                              onClick={() =>
                                                handleGeneratePassword()
                                              }
                                            />
                                          </Tooltip>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Col>
                              )}
                            </Row>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
                <Col lg={3}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>{t(`global.settings`)}</Card.Header>
                        <Card.Body>
                          <SelectStatus
                            active={true}
                            data={data}
                            handleInputChange={handleInputChange}
                          />
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default AdministratorAdd;
